<template>
  <div class="motor-dashboard-card-root p-3">
    <div
      v-for="(item, index) in proposal"
      :key="item.proposal_ui_id || item.journey_id || index"
      class="mb-4 border rounded p-4 shadow-sm bg-white"
    >
      <div class="row align-items-start">
        <div class="col-5 col-md-5 col-lg-4">
          <div
            v-if="isUserActionsVisible && item.role === 'Customer'"
            class="checkbox-container d-flex align-items-center gap-2"
          >
            <input
              :id="`m-${item.journey_id}`"
              type="checkbox"
              class="custom-checkbox"
              :checked="isItemSelected(item)"
              @change="toggleSelection(item, $event.target.checked)"
            />
            <label :for="`m-${item.journey_id}`" class="mb-0">{{ item.journey_id }}</label>
          </div>
          <div v-else class="fw-semibold">
            {{ item.journey_id }}
          </div>
        </div>
        <div class="col-7 col-md-7 col-lg-8 d-flex justify-content-end flex-wrap gap-2">
          <Tag
            :class="{
              'cursor-pointer': isJourneyStatusInteractive(item),
              'cursor-not-allowed': !isJourneyStatusInteractive(item),
            }"
            :severity="getJourneyStatusTagSeverity(item)"
            @click="onStatusClick(item)"
          >
            {{ formatJourneyStatusLabel(item) }}
          </Tag>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-6">
          <div class="mb-3">
            <p class="text-muted mb-1 small">Application Number</p>
            <p class="fw-semibold text-break">{{ applicationNumber(item) }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Customer Name</p>
            <p class="fw-semibold text-break">{{ item.name || '—' }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Line of Business</p>
            <p class="fw-semibold text-break">{{ lineOfBusiness(item) }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Insurance Company</p>
            <p class="fw-semibold text-break">{{ item.insurance_company_code ? String(item.insurance_company_code).toUpperCase() : '—' }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Mobile Number</p>
            <p class="fw-semibold text-break">{{ item.mobile_number || '—' }}</p>
          </div>
        </div>
        <div class="col-6">
          <div class="mb-3">
            <p class="text-muted mb-1 small">Proposal Initiate Date</p>
            <p class="fw-semibold text-break">{{ formatShortDate(item.created_at) }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Premium (₹)</p>
            <p class="fw-semibold text-break">{{ formatPremium(item.total_premium) }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">Payment Date</p>
            <p class="fw-semibold text-break">{{ formatShortDate(item.transaction_date) }}</p>
          </div>
          <div class="mb-3">
            <p class="text-muted mb-1 small">User Name</p>
            <p class="fw-semibold text-break">{{ item.user_name || '—' }}</p>
          </div>
          <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
            <Button
              v-if="retriggerPaymentEnable(item)"
              v-tooltip.top="'Resend payment link to customer'"
              type="button"
              icon="pi pi-wallet"
              rounded
              text
              severity="secondary"
              @click="$emit('retrigger-payment', item)"
            />
            <Button
              v-if="enableUploadDoc(item)"
              v-tooltip.top="'Requirements'"
              type="button"
              icon="pi pi-eye"
              rounded
              text
              severity="secondary"
              @click="$emit('view-requirements', { item, index })"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tag } from 'primevue';
import Button from 'primevue/button';
import { formatToINR } from '@/mixins/utils';

export default {
  name: 'DashboardCard',
  components: { Tag, Button },
  props: {
    proposal: {
      type: Array,
      required: true,
    },
    selectedRecord: {
      type: Array,
      default: () => [],
    },
    isUserActionsVisible: {
      type: Boolean,
      required: true,
    },
    isJourneyStatusInteractive: {
      type: Function,
      required: true,
    },
    formatJourneyStatusLabel: {
      type: Function,
      required: true,
    },
    getJourneyStatusTagSeverity: {
      type: Function,
      required: true,
    },
    retriggerPaymentEnable: {
      type: Function,
      required: true,
    },
    enableUploadDoc: {
      type: Function,
      required: true,
    },
    isInvalidPolicyNumber: {
      type: Function,
      default: null,
    },
  },
  emits: ['resume-journey', 'retrigger-payment', 'view-requirements', 'update:selectedRecord'],
  methods: {
    applicationNumber(item) {
      if (typeof this.isInvalidPolicyNumber === 'function') {
        return !this.isInvalidPolicyNumber(item.policy_number)
          ? item.policy_number
          : item.proposal_manu_id || '—';
      }
      return item.policy_number || item.proposal_manu_id || '—';
    },
    lineOfBusiness(item) {
      return item.line_of_business ? String(item.line_of_business).toUpperCase() : '—';
    },
    formatShortDate(inp) {
      if (!inp) return '—';
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      const d = new Date(Date.parse(inp));
      if (Number.isNaN(d.getTime())) return '—';
      return `${d.getDate()}-${monthNames[d.getMonth()]}-${d.getFullYear()}`;
    },
    formatPremium(val) {
      if (val === null || val === undefined || val === '') return '—';
      const n = Number(val);
      if (Number.isNaN(n)) return '—';
      return formatToINR(n);
    },
    onStatusClick(item) {
      if (this.isJourneyStatusInteractive(item)) {
        this.$emit('resume-journey', item);
      }
    },
    isItemSelected(item) {
      return this.selectedRecord.some((r) => r.journey_id === item.journey_id);
    },
    toggleSelection(item, checked) {
      let next = [...this.selectedRecord];
      if (checked) {
        if (!next.some((r) => r.journey_id === item.journey_id)) {
          next.push(item);
        }
      } else {
        next = next.filter((r) => r.journey_id !== item.journey_id);
      }
      this.$emit('update:selectedRecord', next);
    },
  },
};
</script>

<style scoped>
.fw-semibold {
  word-break: break-word;
  white-space: normal;
  font-weight: 600;
}
.text-muted {
  color: #6c757d !important;
}
.motor-dashboard-card-root :deep(.pointer) {
  cursor: pointer;
}

.motor-dashboard-card-root :deep(.p-tag.cursor-pointer) {
  cursor: pointer !important;
}

.motor-dashboard-card-root :deep(.p-tag.cursor-not-allowed) {
  cursor: not-allowed !important;
}
</style>
